import { useState } from "react";
import { MailWarning, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Increment 23: a slim banner nudging signed-in users to verify their email.
 * Until a real email provider is live (Inc 35), the send endpoint echoes the
 * confirmation link back so UAT users can complete verification in one click.
 */
export function VerifyBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!user || user.emailVerified || user.userType === "admin" || dismissed) return null;

  async function send() {
    setBusy(true); setErr(null); setNote(null);
    try {
      const r = await api.sendVerifyEmail();
      // The account is already verified (session was stale) — refresh so the banner clears.
      if (r?.alreadyVerified) { await refresh?.().catch(() => {}); setDismissed(true); return; }
      setSent(true);
      if (r?.throttled) setNote("A verification link was sent recently — please check your inbox (and spam) before requesting another.");
      if (r?.verifyUrl) setLink(r.verifyUrl);
    } catch (e: any) {
      setErr(e?.message || "We couldn't send the verification email right now. Please try again in a moment.");
    } finally { setBusy(false); }
  }

  // If the server echoed a link (no real email yet), turn it into a relative path
  // so it navigates within the SPA.
  const relLink = link ? link.replace(/^https?:\/\/[^/]+/, "") : null;

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="container-x py-2 flex items-center gap-3 text-sm text-amber-900">
        <MailWarning className="w-4 h-4 shrink-0" />
        <div className="flex-1 min-w-0">
          {!sent && <span>Verify your email to unlock posting, bidding and orders.</span>}
          {sent && !relLink && note && <span>{note}</span>}
          {sent && !relLink && !note && <span>Verification email sent to <b>{user.email}</b>. Check your inbox (and spam folder).</span>}
          {sent && relLink && (
            <span>
              UAT test shortcut (in production this link is emailed only to <b>{user.email}</b>) —{" "}
              <a href={relLink} className="font-semibold underline">confirm your email now</a>.
            </span>
          )}
          {err && <span className="text-brand-red"> {err}</span>}
        </div>
        {!sent && (
          <button onClick={send} disabled={busy} className="btn-ghost h-7 px-3 text-xs font-semibold border border-amber-300 disabled:opacity-60">
            {busy ? "Sending…" : "Send verification"}
          </button>
        )}
        <button onClick={() => setDismissed(true)} className="p-1 hover:bg-amber-100 rounded" title="Dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
